import { useMemo } from 'react'
import { orderBy } from 'lodash'
import BigNumber from 'bignumber.js'
import { useWeb3React } from '@web3-react/core'
import { useTokenBalances } from '~/state/wallet/hooks'
import { useLimitSwapRecommendTokens } from '~/state/dex/hooks'
import { useTokensPrice } from '~/hooks/useTokenPrice'
import TokenRecommendationsBox from '~/components/TokenListModal/components/TokenRecommendationsBox'
import { getAddress } from 'viem'
import { ENV } from '~/constants/env'
import { FixedPointNumber } from '~/utils/fixedPoint'
import { WETH as WETHAddress } from '~/constants/addresses'
import { Mainnet, Sepolia } from '~/model/chain/ethereum'
import { OrderedLimitSwapToken } from '~/components/LimitOrder/hooks/useOrderedTokenList'

interface TokenRecommendationsProps {
  selectedChainId: number | null
  onTokenSelected: (token: LimitSwapToken) => void
}

const TokenRecommendations: React.FC<TokenRecommendationsProps> = ({
  selectedChainId,
  onTokenSelected,
}) => {
  const { account, chainId: currentChainId } = useWeb3React()
  const isConnected = !!account && currentChainId === selectedChainId
  const isSelectedAll = selectedChainId === null

  const tokens = useLimitSwapRecommendTokens(selectedChainId)
  const tokenBalances = useTokenBalances(account, isConnected ? tokens : [])
  const prices = useTokensPrice(
    isConnected ? tokens.map((token) => token.address) : [],
  )

  const sortedTokens: OrderedLimitSwapToken[] = useMemo(() => {
    let sortedResult = []
    const tokensSortBySymbol = orderBy(
      tokens,
      ['chainId', ({ symbol }) => symbol.toLowerCase()],
      ['asc', 'asc'],
    )
    sortedResult = tokensSortBySymbol

    if (isConnected) {
      const tokensWithPrice = tokensSortBySymbol.map((token) => {
        const address = getAddress(token.address)
        const priceUSD = new BigNumber(prices[address] || 0)
          .multipliedBy(tokenBalances[token.address]?.toDecimalsFixed() || 0)
          .toFixed(2)

        return {
          ...token,
          balance: tokenBalances[token.address] || new FixedPointNumber(0),
          price: prices[address],
          priceUSD: isNaN(Number(priceUSD)) ? '0' : priceUSD,
        } as OrderedLimitSwapToken
      })
      sortedResult = orderBy(tokensWithPrice, ['priceUSD'], ['desc'])
    }

    // move weth to the first position
    const isDev = ENV.development || ENV.local
    const firstChainId = isDev ? Sepolia.chainId : Mainnet.chainId
    const wethIndex = sortedResult.findIndex(
      (token) =>
        token.address.toLowerCase() ===
        WETHAddress[firstChainId]?.toLowerCase(),
    )
    if (wethIndex > -1) {
      const weth = sortedResult[wethIndex]
      sortedResult.splice(wethIndex, 1)
      sortedResult.unshift(weth)
    }

    // display first 3 tokens of the first chain and 4 tokens of the reset chain
    const firstArr = []
    const resetArr = []
    sortedResult.forEach((token) => {
      if (token.chainId === firstChainId) {
        firstArr.push(token)
      } else {
        resetArr.push(token)
      }
    })
    return [...firstArr.slice(0, 3), ...resetArr.slice(0, 4)]
  }, [tokens, tokenBalances, prices, isConnected, ENV])

  return (
    <TokenRecommendationsBox
      recommendTokens={sortedTokens}
      onTokenSelect={onTokenSelected}
      showNetworkBadge={isSelectedAll}
    />
  )
}

export default TokenRecommendations
